import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import {
  FlowContainer,
  FlowNode,
  FlowArrow,
  FlowTerminal,
  FlowNote,
  FlowParallel,
} from '../FlowPrimitives';

// ============================================================================
// Experiment 2 - The Purification and Analysis of Water
// Cross-referenced with CHEM2131 Lab Manual, Experiment 2
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp2',
  title: 'The Purification and Analysis of Water',
  objectives: [
    'Learn basic water purification techniques using lime and alum treatment',
    'Perform qualitative analysis of 8 water samples using conductivity, precipitate tests, flame tests, and EDTA hardness titration',
    'Compare properties of different water sources (tap, 0.1 M NaCl, distilled, reverse osmosis, spring, H⁺ exchange, Brita filtered, home tap)',
  ],
  safetyNotes: [
    'Quicklite (CaO) is caustic - avoid skin and eye contact; wash immediately if exposed',
    'Silver nitrate (AgNO₃) stains skin black - wear gloves when handling',
    'Handle all chemicals with care and follow standard laboratory practices',
  ],
  equipment: [
    'Two 600 mL beakers',
    'Graduated cylinder (100 mL)',
    'Stirring rod',
    'Red and blue litmus paper',
    'Conductivity meter',
    'Nichrome wire loop',
    'Bunsen burner',
  ],
  chemicals: [
    'Lime (CaO) solution or solid CaO',
    'Alum solution (KAl(SO₄)₂ or NH₄Al(SO₄)₂)',
    'Soil (10.0 g)',
    '0.1 M AgNO₃ solution (chloride test)',
    '0.1 M BaCl₂ solution (sulfate test)',
    '0.00500 M EDTA solution (hardness titration)',
    'Ammonia-ammonium chloride buffer',
    'Eriochrome black T indicator',
    '6 M HCl (flame test wire cleaning)',
    '0.1 M NaCl solution (water sample b)',
    'Hydrogen exchange resin',
  ],
  procedure: [
    {
      number: 1,
      title: 'Part I: Prepare Muddy Water',
      details: [
        'Label two beakers as "Control" and "Experimental"',
        'Fill the experimental beaker three quarters full with water and add 10.0 g of soil',
        'Stir until a muddy solution results',
        'Pour half of this muddy water into the control beaker and set it aside',
      ],
      manualRef: 'Lab Manual - Part I, Steps 1-4',
    },
    {
      number: 2,
      title: 'Part I: Split and Treat',
      details: [
        'Add 1 dropper full of lime solution (or match-head size solid CaO) to the experimental beaker. Stir thoroughly.',
        'Dip red litmus paper - if it does not turn blue, add more lime until it does',
        'Add 1 dropper full of alum solution. Continue adding until a thick, white, gelatinous precipitate forms.',
      ],
      safetyNote: 'Lime (CaO) is caustic - avoid skin contact',
      manualRef: 'Lab Manual - Part I, Steps 5-7',
    },
    {
      number: 3,
      title: 'Part I: Flocculation and Settling',
      details: [
        'Stir the water thoroughly after adding alum',
        'Place the two beakers side-by-side and observe them for several minutes',
        'Record and discuss your observations',
        'Expected: Control remains cloudy; Experimental is much clearer with sediment at bottom',
      ],
      manualRef: 'Lab Manual - Part I, Steps 8-10',
    },
    {
      number: 4,
      title: 'Part II: Conductivity Test',
      details: [
        'Test each water sample (tap, 0.1 M NaCl, distilled, RO, spring, H⁺ exchange, Brita, home tap) with conductivity device',
        'Record whether the LED lights up (indicating dissolved ions)',
        'Compare conductivity across all samples',
      ],
      manualRef: 'Lab Manual - Part II, Test 1',
    },
    {
      number: 5,
      title: 'Part II: Flame Test',
      details: [
        'Clean platinum or nichrome wire by alternately dipping into 6 M HCl and holding in flame',
        'Put a few drops of each sample into clean test tubes, then test in flame',
        'Clean wire between each test. Do NOT put wire into original samples (HCl contamination)',
        'Record flame color: Na⁺ = yellow-orange, K⁺ = lilac, Ca²⁺ = brick red',
      ],
      manualRef: 'Lab Manual - Part II, Test 2',
    },
    {
      number: 6,
      title: 'Part II: Chloride Test (AgNO₃)',
      details: [
        'Add a few drops of 0.1 M AgNO₃ to ~0.5 mL of each sample in 13×100 mm test tubes',
        'White precipitate indicates chloride ions: Ag⁺ + Cl⁻ → AgCl(s)',
      ],
      safetyNote: 'AgNO₃ stains skin black - wear gloves',
      manualRef: 'Lab Manual - Part II, Test 3',
    },
    {
      number: 7,
      title: 'Part II: Sulfate Test (BaCl₂)',
      details: [
        'Add a few drops of 0.1 M BaCl₂ to ~0.5 mL of each sample in 13×100 mm test tubes',
        'White precipitate indicates sulfate ions: Ba²⁺ + SO₄²⁻ → BaSO₄(s)',
      ],
      manualRef: 'Lab Manual - Part II, Test 4',
    },
    {
      number: 8,
      title: 'Part II: EDTA Hardness Titration',
      details: [
        'Pipet 5.00 mL of water sample into a large test tube',
        'Add 4 drops ammonia-ammonium chloride buffer and 1 drop eriochrome black T indicator. Stir.',
        'Titrate with 0.00500 M EDTA using a dropper. Count drops for color change: pink-red → blue',
        'Hardness (ppm) ≈ (5)(# of drops). Range: 0-100 drops depending on sample.',
      ],
      manualRef: 'Lab Manual - Part II, Test 5',
    },
  ],
  keyFormulas: [
    'Ag⁺ + Cl⁻ → AgCl(s)  [Chloride precipitation test]',
    'Ba\u00B2⁺ + SO₄\u00B2⁻ → BaSO₄(s)  [Sulfate precipitation test]',
    'Ca\u00B2⁺/Mg\u00B2⁺ + EDTA → complex  [Hardness titration]',
  ],
  tips: [
    'Stir the alum gently - vigorous stirring breaks up flocs and slows settling',
    'Do not disturb the beakers during the settling period',
    'Clean the nichrome wire by flaming it until no color is visible before testing the next sample',
    'Always test the blank/distilled water first to establish a baseline',
    'The seawater sample should show the highest conductivity and strongest chloride test',
  ],
};

// ============================================================================
// Flowchart: Water Purification & Analysis Procedure
// Layout: Two parallel tracks - Part I (left) and Part II (right)
// Total width: 700px, height: 680px
// ============================================================================

function Flowchart() {
  // Part I column center: x=70 (nodes at x=70, width=180 -> spans 70..250, center at 160)
  const p1x = 70;
  // Part II column center: x=400 (nodes at x=400, width=200 -> spans 400..600, center at 500)
  const p2x = 400;
  const nw = 180;
  const nh = 56;

  return (
    <FlowContainer width={750} height={720} title="Water Purification & Analysis Procedure Flow">
      {/* ---- Part I: Purification (left column) ---- */}
      <FlowTerminal x={90} y={44} label="Part I: Purify" color="#6366f1" />
      <FlowArrow from={{ x: 160, y: 80 }} to={{ x: 160, y: 100 }} />

      <FlowNode x={p1x} y={100} title="Label Beakers" subtitle="Control & Experimental" color="#0d9488" />
      <FlowArrow from={{ x: 160, y: 156 }} to={{ x: 160, y: 176 }} />

      <FlowNode x={p1x} y={176} title="Add Water + Soil" subtitle="3/4 full water, 10.0 g soil" color="#0d9488" />
      <FlowArrow from={{ x: 160, y: 232 }} to={{ x: 160, y: 252 }} />

      <FlowNode x={p1x} y={252} title="Stir & Split" subtitle="Pour half into control bea..." color="#0d9488" />
      <FlowArrow from={{ x: 160, y: 308 }} to={{ x: 160, y: 328 }} />

      <FlowNode x={p1x} y={328} title="Add Lime (CaO)" subtitle="1 dropper full to experimental" color="#2563eb" />
      <FlowNote x={10} y={395} text="Caustic - gloves!" width={110} color="#ef4444" />
      <FlowArrow from={{ x: 160, y: 384 }} to={{ x: 160, y: 404 }} />

      <FlowNode x={p1x} y={404} title="Test pH (Litmus)" subtitle="Red litmus should turn blue" color="#0d9488" />
      <FlowArrow from={{ x: 160, y: 460 }} to={{ x: 160, y: 480 }} />

      <FlowNode x={p1x} y={480} title="Add Alum & Stir" subtitle="1 dropper full, add until ppt" color="#2563eb" />
      <FlowArrow from={{ x: 160, y: 536 }} to={{ x: 160, y: 556 }} />

      <FlowNode x={p1x} y={556} title="Settle & Compare" subtitle="Observe several minutes, co..." color="#0d9488" />
      <FlowArrow from={{ x: 160, y: 612 }} to={{ x: 160, y: 650 }} />

      <FlowTerminal x={90} y={650} label="Part I Done" color="#6366f1" />

      {/* ---- Part II: Analysis (right column) ---- */}
      <FlowTerminal x={430} y={44} label="Part II: Analyze" color="#6366f1" />
      <FlowArrow from={{ x: 500, y: 80 }} to={{ x: 500, y: 100 }} />

      <FlowNode x={p2x} y={100} width={200} title="Collect 8 Samples" subtitle="Tap, NaCl, distilled, RO, ..." color="#7c3aed" />
      <FlowArrow from={{ x: 500, y: 156 }} to={{ x: 500, y: 186 }} />

      {/* Parallel tests block */}
      <FlowParallel
        x={p2x}
        y={186}
        width={200}
        items={[
          'Test 1: Conductivity',
          'Test 2: Flame Test',
          'Test 3: Chloride (AgNO₃)',
          'Test 4: Sulfate (BaCl₂)',
          'Test 5: EDTA Hardness',
        ]}
        color="#7c3aed"
      />
      {/* Parallel block height: 5 items * 28 + 8 = 148 */}

      <FlowNote x={p2x + 210} y={220} text="Na+: yellow-orange" width={120} color="#f59e0b" />
      <FlowNote x={p2x + 210} y={260} text="AgNO₃ stains skin!" width={120} color="#ef4444" />

      <FlowArrow from={{ x: 500, y: 334 }} to={{ x: 500, y: 364 }} />

      <FlowNode x={p2x} y={364} width={200} title="Record All Results" subtitle="Compare across water sources" color="#7c3aed" />
      <FlowArrow from={{ x: 500, y: 420 }} to={{ x: 500, y: 450 }} />

      <FlowNode x={p2x} y={450} width={200} title="Identify Ions Present" subtitle="Which sample has most ions?" color="#7c3aed" />
      <FlowArrow from={{ x: 500, y: 506 }} to={{ x: 500, y: 650 }} />

      <FlowTerminal x={430} y={650} label="Part II Done" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp2Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
